/**
 * Group identity primitives — Signal's "group master key" model, web port.
 *
 * A group has a 32-byte master key known only to current members. From it a
 * 16-byte "public id" is derived (HKDF-SHA256). The server stores the group
 * log indexed by public id but cannot synthesise it without the master key.
 * The same master key derives an HMAC signing key used to authenticate group
 * state changes (membership, role updates, etc.) so the server cannot rewrite
 * group history.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/GroupIdentity.swift` byte-for-byte on
 * the wire; HKDF info strings and salt are identical so iOS and web derive
 * the same keys from the same master key.
 *
 * All SubtleCrypto operations are async; the module exposes a thin
 * synchronous wrapper layer (`GroupMasterKey`, `GroupPublicId`) that holds raw
 * bytes and async free-functions for derivation and signing.
 */

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export class GroupIdentityError extends Error {
  constructor(
    public readonly kind:
      | "invalidMasterKeyLength"
      | "invalidPublicIdLength"
      | "invalidInviteLink"
      | "publicIdDoesNotMatchMasterKey",
    message: string,
  ) {
    super(message);
    this.name = "GroupIdentityError";
  }
}

// ---------------------------------------------------------------------------
// Value objects
// ---------------------------------------------------------------------------

/**
 * 32-byte secret known only to current group members. Generated locally by
 * the group creator and distributed via invite link or pairwise E2EE. The
 * server never sees this value.
 */
export class GroupMasterKey {
  readonly raw: Uint8Array;

  constructor(raw: Uint8Array) {
    if (raw.length !== 32) {
      throw new GroupIdentityError(
        "invalidMasterKeyLength",
        `GroupMasterKey must be 32 bytes; got ${raw.length}`,
      );
    }
    // Defensive copy — caller cannot mutate our internal state.
    this.raw = raw.slice();
  }

  equals(other: GroupMasterKey): boolean {
    if (this.raw.length !== other.raw.length) return false;
    // Constant-time-ish comparison (good enough for tests; real HMAC verify
    // uses SubtleCrypto which is CT).
    let diff = 0;
    for (let i = 0; i < this.raw.length; i++) {
      diff |= this.raw[i] ^ other.raw[i];
    }
    return diff === 0;
  }

  /**
   * Deterministic 32-byte key for tests, filled with `seed & 0xff`. Mirrors
   * iOS `freshMasterKey(seed: UInt8)` = `Data(repeating: seed, count: 32)`.
   * Each test case uses a distinct seed to avoid MAC cross-contamination.
   */
  static fromSeed(seed: number): GroupMasterKey {
    return new GroupMasterKey(new Uint8Array(32).fill(seed & 0xff));
  }
}

/**
 * 16-byte deterministic identifier derivable from the master key. Shared with
 * the server as an opaque handle for the group log.
 */
export class GroupPublicId {
  readonly raw: Uint8Array;

  constructor(raw: Uint8Array) {
    if (raw.length !== 16) {
      throw new GroupIdentityError(
        "invalidPublicIdLength",
        `GroupPublicId must be 16 bytes; got ${raw.length}`,
      );
    }
    this.raw = raw.slice();
  }

  equals(other: GroupPublicId): boolean {
    if (this.raw.length !== other.raw.length) return false;
    let diff = 0;
    for (let i = 0; i < this.raw.length; i++) {
      diff |= this.raw[i] ^ other.raw[i];
    }
    return diff === 0;
  }

  /** base64url with no padding — safe for URLs and QR codes. */
  asString(): string {
    return base64UrlEncode(this.raw);
  }
}

// ---------------------------------------------------------------------------
// HKDF info strings (CRITICAL — must match iOS byte-for-byte)
// ---------------------------------------------------------------------------

const INFO_PUBLIC_ID = "kaether:group:public-id:v1";
const INFO_SIGN = "kaether:group:sign:v1";

/** 32-byte all-zeros salt used for all HKDF derivations. Matches iOS. */
const HKDF_SALT = new Uint8Array(32);

// ---------------------------------------------------------------------------
// Internal HKDF helper
// ---------------------------------------------------------------------------

/**
 * HKDF-SHA256(ikm=masterKey, salt=zeros32, info=infoString, length=outputLen)
 * via SubtleCrypto.
 */
async function hkdfDerive(
  masterKey: GroupMasterKey,
  infoString: string,
  outputLen: number,
): Promise<Uint8Array> {
  const ikm = await crypto.subtle.importKey(
    "raw",
    masterKey.raw,
    { name: "HKDF" },
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: HKDF_SALT,
      info: new TextEncoder().encode(infoString),
    },
    ikm,
    outputLen * 8,
  );
  return new Uint8Array(bits);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Cryptographically-secure 32-byte random master key via
 * `crypto.getRandomValues`.
 */
export async function generateGroup(): Promise<{
  masterKey: GroupMasterKey;
  publicId: GroupPublicId;
}> {
  const raw = new Uint8Array(32);
  crypto.getRandomValues(raw);
  const masterKey = new GroupMasterKey(raw);
  const publicId = await derivePublicId(masterKey);
  return { masterKey, publicId };
}

/**
 * HKDF-SHA256(masterKey, salt=zeros32, info="kaether:group:public-id:v1")
 * truncated to 16 bytes. Deterministic — every member of a group derives the
 * same public id from the same master key.
 */
export async function derivePublicId(
  masterKey: GroupMasterKey,
): Promise<GroupPublicId> {
  const raw = await hkdfDerive(masterKey, INFO_PUBLIC_ID, 16);
  return new GroupPublicId(raw);
}

/**
 * HKDF-SHA256(masterKey, salt=zeros32, info="kaether:group:sign:v1") →
 * 32-byte HMAC key. Internal helper for sign/verify.
 */
async function deriveSigningKey(
  masterKey: GroupMasterKey,
): Promise<CryptoKey> {
  const rawKey = await hkdfDerive(masterKey, INFO_SIGN, 32);
  return crypto.subtle.importKey(
    "raw",
    rawKey,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"],
  );
}

/**
 * HMAC-SHA256 over `canonicalBytes` under the group signing key. Returns a
 * 32-byte MAC. Used to authenticate group state changes so the server cannot
 * rewrite group history.
 */
export async function sign(
  masterKey: GroupMasterKey,
  canonicalBytes: Uint8Array,
): Promise<Uint8Array> {
  const signingKey = await deriveSigningKey(masterKey);
  const mac = await crypto.subtle.sign("HMAC", signingKey, canonicalBytes);
  return new Uint8Array(mac);
}

/**
 * Constant-time MAC verification via SubtleCrypto. Returns false on length
 * mismatch or wrong key — never throws for mismatched inputs.
 *
 * SubtleCrypto's HMAC verify is constant-time over the MAC bytes by
 * specification, so timing attacks cannot learn anything about the expected
 * MAC.
 */
export async function verify(
  masterKey: GroupMasterKey,
  signature: Uint8Array,
  canonicalBytes: Uint8Array,
): Promise<boolean> {
  if (signature.length !== 32) return false;
  try {
    const signingKey = await deriveSigningKey(masterKey);
    return await crypto.subtle.verify(
      "HMAC",
      signingKey,
      signature,
      canonicalBytes,
    );
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Consumer-facing aliases
// ---------------------------------------------------------------------------
// `group-roles/GroupRole.ts` and `group-membership/GroupMembershipEvent.ts`
// were authored against an earlier API draft that named the HMAC primitives
// `signWithGroupKey` / `verifyWithGroupKey` with `(canonicalBytes, masterKey)`
// argument order, and accessed them via a `GroupIdentity` namespace object.
// The implementation landed as `sign` / `verify` with the opposite arg order.
// These aliases bridge the two without rewriting the consumers.

export async function signWithGroupKey(
  canonicalBytes: Uint8Array,
  masterKey: GroupMasterKey,
): Promise<Uint8Array> {
  return sign(masterKey, canonicalBytes);
}

export async function verifyWithGroupKey(
  canonicalBytes: Uint8Array,
  signature: Uint8Array,
  masterKey: GroupMasterKey,
): Promise<boolean> {
  return verify(masterKey, signature, canonicalBytes);
}

export const GroupIdentity = {
  sign,
  verify,
  signWithGroupKey,
  verifyWithGroupKey,
  derivePublicId,
  generateGroup,
};

// ---------------------------------------------------------------------------
// Helpers (also used by GroupInviteLink)
// ---------------------------------------------------------------------------

export function base64UrlEncode(data: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

export function base64UrlDecode(s: string): Uint8Array | null {
  try {
    const padded =
      s.replace(/-/g, "+").replace(/_/g, "/") +
      "====".slice((s.length % 4 === 0 ? 4 : s.length % 4));
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}
